import { SkyButton, SkyLoader, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useLocation } from "wouter";
import { navigate as navigateBrowser } from "wouter/use-browser-location";
import { TenantInstancePicker } from "@/components/navigation/TenantInstancePicker";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { usePermissions } from "@/hooks/use-permissions";
import { FORM_MAX_WIDTH } from "@/lib/layout";
import { ROUTES } from "@/lib/routes";
import type { PermissionKey } from "@shared/permissions";
import { useInstanceRefusal, type InstanceRefusalCode } from "./instanceRefusal";

/** One sentence for an id that is malformed, unknown, disabled or forbidden: never told apart. */
export const INSTANCE_UNAVAILABLE_TITLE =
  "You don't have access to this instance, or it doesn't exist";

/**
 * #384: what a person who holds a role there reads when the instance or its tenant is out of
 * service: the plain reason and who to ask. Never the tenant's name, so the page says nothing a
 * link could not already say.
 */
export const INSTANCE_REFUSAL_WORDS: Record<
  InstanceRefusalCode,
  { title: string; message: string }
> = {
  INSTANCE_DISABLED: {
    title: "This instance is disabled",
    message: "Ask your Skypoint contact to enable it.",
  },
  TENANT_DISABLED: {
    title: "This tenant is disabled",
    message:
      "None of its instances can be opened while it is disabled. Ask your Skypoint contact to enable it.",
  },
  TENANT_DELETED: {
    title: "This tenant was deleted",
    message:
      "None of its instances can be opened again. Ask your Skypoint contact if you have questions.",
  },
};

/**
 * #384 review: the words for a reader who can put it right from the platform screens (a
 * PlatformAdmin is the Skypoint contact the other words name), with the page that does it. Each
 * applies only while the reader holds `permission`; anyone else reads `INSTANCE_REFUSAL_WORDS`.
 */
export const INSTANCE_REFUSAL_ADMIN_WORDS: Record<
  InstanceRefusalCode,
  {
    permission: PermissionKey;
    title: string;
    message: string;
    action: { label: string; path: string };
  }
> = {
  INSTANCE_DISABLED: {
    permission: "platform.instances.manage",
    title: "This instance is disabled",
    message: "You can enable it on the Instances page.",
    action: { label: "Open Instances", path: ROUTES.adminInstances },
  },
  TENANT_DISABLED: {
    permission: "platform.tenants.manage",
    title: "This tenant is disabled",
    message:
      "None of its instances can be opened while it is disabled. You can enable it on the Tenants page.",
    action: { label: "Open Tenants", path: ROUTES.adminTenants },
  },
  TENANT_DELETED: {
    permission: "platform.tenants.manage",
    title: "This tenant was deleted",
    message:
      "None of its instances can be opened again. Its offboarding list is on the Tenants page, under the Deleted filter.",
    action: { label: "Open Tenants", path: ROUTES.adminTenants },
  },
};

/**
 * An address whose instance this person cannot open (round 9 T-58), in the shell: the top bar,
 * the user menu and the nav stay, so the page is a way on, never a dead end. It says the same
 * thing whether the instance is forbidden, disabled, unknown or the id is malformed, so it never
 * tells anyone that an instance they cannot reach exists. It reads nothing of that instance: the
 * selection is empty, so no request carries its id.
 *
 * #384: the one exception is a person who holds a role there while the instance or its tenant is
 * disabled, or the tenant deleted: the server tells them (`useInstanceRefusal`) and the page says
 * so in plain words, with who to ask. Anyone else still reads the one sentence above.
 *
 * The way on: the tenant and instance switcher from the user menu, and the instance this tab was
 * last in, at the same screen when that instance allows it.
 */
export function InstanceUnavailable() {
  const { lastVisitedInstance, selectInstance, addressInstance } = useTenantInstance();
  const [location] = useLocation();
  const { can } = usePermissions();
  const refusal = useInstanceRefusal(addressInstance?.id ?? null);

  if (refusal.pending) {
    return <SkyLoader label="Checking your access to this instance…" testId="instance-checking" />;
  }
  const adminWords = refusal.code ? INSTANCE_REFUSAL_ADMIN_WORDS[refusal.code] : null;
  const fixable = adminWords !== null && can(adminWords.permission) ? adminWords : null;
  const words = fixable ?? (refusal.code ? INSTANCE_REFUSAL_WORDS[refusal.code] : null);

  return (
    <div
      className={`flex flex-col gap-4 ${FORM_MAX_WIDTH}`}
      data-testid="instance-unavailable"
      data-location={location}
      data-refusal={refusal.code ?? undefined}
    >
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="instance-unavailable-message"
        title={words?.title ?? INSTANCE_UNAVAILABLE_TITLE}
        message={
          words?.message ?? "Check the link you followed, or choose an instance you can open."
        }
      />
      {fixable ? (
        <div>
          <SkyButton
            appearance="primary"
            testId="instance-unavailable-fix"
            // A platform screen, outside the instance address this page is nested in.
            onClick={() => navigateBrowser(fixable.action.path)}
          >
            {fixable.action.label}
          </SkyButton>
        </div>
      ) : null}
      {lastVisitedInstance ? (
        <div>
          <SkyButton
            appearance={fixable ? "secondary" : "primary"}
            testId="instance-unavailable-last"
            onClick={() => selectInstance(lastVisitedInstance.id)}
          >
            {`Go to ${lastVisitedInstance.tenantDisplayName} · ${lastVisitedInstance.name}`}
          </SkyButton>
        </div>
      ) : null}
      <section aria-labelledby="instance-unavailable-switch" className="flex flex-col gap-2">
        <SkyText as="h2" id="instance-unavailable-switch" size={400} weight="semibold" block>
          Switch instance
        </SkyText>
        <TenantInstancePicker />
      </section>
    </div>
  );
}
